<div class="container h-full flex flex-col mx-auto px-4 md:px-6 pt-8 pb-4">
  <div class="flex items-center mb-4 min-h-10">
    <h2 class="text-2xl font-medium">Variabelen</h2>
    <button
      *appHasPermission="'var:create'"
      mat-button
      class="ml-auto"
      type="button"
      (click)="openAddForm()"
      [disabled]="showAddForm()"
    >
      <mat-icon>add</mat-icon>
      Nieuwe variabele
    </button>
  </div>

  @if (showAddForm()) {
    <form
      class="bg-white border border-blue-200 rounded-lg p-4 mb-4"
      [formGroup]="addForm"
      (ngSubmit)="handleAdd()"
    >
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div class="space-y-1">
          <label class="block font-medium text-sm select-none" for="add-key"
            >Sleutel</label
          >
          <mat-form-field class="w-full">
            <input
              matInput
              formControlName="key"
              id="add-key"
              autocomplete="off"
              placeholder="naam_variabele"
              type="text"
            />
            @if (addForm.controls.key.hasError('required')) {
              <mat-error>Verplicht</mat-error>
            } @else if (addForm.controls.key.hasError('pattern')) {
              <mat-error>Alleen kleine letters, cijfers en underscores</mat-error>
            } @else if (addForm.controls.key.hasError('maxlength')) {
              <mat-error>Max. 50 karakters</mat-error>
            }
          </mat-form-field>
        </div>

        <div class="space-y-1">
          <label class="block font-medium text-sm select-none" for="add-value"
            >Waarde</label
          >
          <mat-form-field class="w-full">
            <input
              matInput
              formControlName="value"
              id="add-value"
              autocomplete="off"
              type="text"
            />
            @if (addForm.controls.value.hasError('required')) {
              <mat-error>Verplicht</mat-error>
            }
          </mat-form-field>
        </div>

        <div class="space-y-1">
          <label class="block font-medium text-sm select-none" for="add-description"
            >Beschrijving</label
          >
          <mat-form-field class="w-full">
            <input
              matInput
              formControlName="description"
              id="add-description"
              autocomplete="off"
              type="text"
            />
          </mat-form-field>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-2">
        <button mat-stroked-button type="button" (click)="cancelAdd()">
          Annuleren
        </button>
        <button
          mat-flat-button
          type="submit"
          [disabled]="addForm.invalid || submitting()"
        >
          Toevoegen
        </button>
      </div>
    </form>
  }

  <div class="bg-white p-4 rounded-t-lg">
    <mat-form-field
      subscriptSizing="dynamic"
      class="w-full [--mat-form-field-container-text-size:14px] [--mat-form-field-container-height:48px] [--mat-form-field-container-vertical-padding:12px]"
    >
      <mat-icon matPrefix> search </mat-icon>

      <input
        #searchInput
        matInput
        placeholder="Zoeken op sleutel, waarde of beschrijving..."
        [disabled]="hasNoData()"
        [value]="keyword() ?? ''"
        (input)="handleSearch(searchInput.value)"
      />

      @if (keyword()) {
        <button mat-icon-button matSuffix class="mr-1" (click)="clearSearch(); searchInput.value = ''">
          <mat-icon>cancel</mat-icon>
        </button>
      }
    </mat-form-field>
  </div>

  <app-data-table-container class="rounded-b-lg! flex-1 min-h-0">
    <table
      mat-table
      matSort
      [matSortActive]="sortActive()"
      [matSortDirection]="sortDirection()"
      (matSortChange)="handleSort($event)"
      [dataSource]="paginatedVars()"
    >
      <ng-container matColumnDef="key" sticky>
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Sleutel</th>
        <td mat-cell *matCellDef="let element" class="font-mono text-xs">
          <code class="bg-gray-100 px-1.5 py-0.5 rounded-sm text-gray-800">[{{ element.key }}]</code>
        </td>
      </ng-container>

      <ng-container matColumnDef="value">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Waarde</th>
        <td mat-cell *matCellDef="let element">
          @if (editingVarId() === element.id) {
            <mat-form-field
              subscriptSizing="dynamic"
              class="w-full [--mat-form-field-container-text-size:14px] [--mat-form-field-container-height:36px] [--mat-form-field-container-vertical-padding:6px]"
            >
              <input
                matInput
                [formControl]="editForm.controls.value"
                autocomplete="off"
                type="text"
              />
            </mat-form-field>
          } @else {
            <span
              class="w-min max-w-60 truncate block"
              [matTooltip]="element.value"
              matTooltipPosition="after"
            >{{ element.value }}</span>
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="description">
        <th mat-header-cell *matHeaderCellDef>Beschrijving</th>
        <td mat-cell *matCellDef="let element">
          @if (editingVarId() === element.id) {
            <mat-form-field
              subscriptSizing="dynamic"
              class="w-full [--mat-form-field-container-text-size:14px] [--mat-form-field-container-height:36px] [--mat-form-field-container-vertical-padding:6px]"
            >
              <input
                matInput
                [formControl]="editForm.controls.description"
                autocomplete="off"
                type="text"
              />
            </mat-form-field>
          } @else {
            {{ element.description ?? '—' }}
          }
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef></th>
        <td mat-cell *matCellDef="let element">
          @if (editingVarId() === element.id) {
            <div class="flex gap-1 justify-end">
              <button
                mat-icon-button
                type="button"
                matTooltip="Opslaan"
                [disabled]="editForm.invalid || submitting()"
                (click)="handleEdit()"
              >
                <mat-icon>check</mat-icon>
              </button>
              <button
                mat-icon-button
                type="button"
                matTooltip="Annuleren"
                (click)="cancelEdit()"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
          } @else {
            <div class="flex gap-1 justify-end">
              <button
                *appHasPermission="'var:update'"
                mat-icon-button
                type="button"
                matTooltip="Bewerken"
                (click)="startEdit(element)"
              >
                <mat-icon>edit</mat-icon>
              </button>
              <button
                *appHasPermission="'var:delete'"
                mat-icon-button
                type="button"
                matTooltip="Verwijderen"
                (click)="handleDelete(element)"
              >
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          }
        </td>
      </ng-container>

      <tr
        mat-header-row
        *matHeaderRowDef="displayedColumns; sticky: true"
        class="whitespace-nowrap"
      ></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        class="hover:bg-gray-100! whitespace-nowrap"
      ></tr>

      <tr class="mat-mdc-row" *matNoDataRow>
        <td
          class="mat-mdc-cell mdc-data-table__cell !border-none"
          [colSpan]="displayedColumns.length"
        >
          @if (keyword()) {
            Geen variabelen gevonden voor "{{ keyword() }}"
          } @else {
            Nog geen variabelen aangemaakt. Klik op "Nieuwe variabele" om te beginnen.
          }
        </td>
      </tr>
    </table>

    <mat-paginator
      class="border-t border-(--mat-table-row-item-outline-color)"
      (page)="handlePageChange($event)"
      [length]="filteredVars().length"
      [pageSize]="pageSize()"
      [pageIndex]="page()"
      [pageSizeOptions]="[25, 50, 100]"
    ></mat-paginator>
  </app-data-table-container>

  @if (filteredPendingVars().length > 0) {
    <div class="mt-6">
      <button
        type="button"
        class="flex items-center gap-1 w-full text-left cursor-pointer mb-3 group"
        (click)="togglePending()"
      >
        <mat-icon class="text-gray-400! transition-transform duration-200" [class.rotate-90]="pendingExpanded()">
          keyboard_arrow_right
        </mat-icon>
        <h3 class="text-lg font-medium text-gray-500">
          Wachtend op verwijdering
        </h3>
        <span class="ml-1.5 inline-flex items-center justify-center bg-red-100 text-red-600 text-xs font-medium rounded-full px-2 py-0.5 min-w-[1.25rem]">
          {{ filteredPendingVars().length }}
        </span>
      </button>

      <div
        class="grid transition-[grid-template-rows] duration-200 ease-out"
        [style.grid-template-rows]="pendingExpanded() ? '1fr' : '0fr'"
      >
      <div class="overflow-hidden min-h-0">
        <app-data-table-container class="rounded-lg! max-h-[40vh]">
          <table mat-table matSort (matSortChange)="handlePendingSort($event)" [dataSource]="filteredPendingVars()">
            <ng-container matColumnDef="key" sticky>
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Sleutel</th>
              <td mat-cell *matCellDef="let element" class="font-mono text-xs text-gray-500">
                <code class="bg-gray-100 px-1.5 py-0.5 rounded-sm text-gray-800">[{{ element.key }}]</code>
              </td>
            </ng-container>

            <ng-container matColumnDef="value">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Waarde</th>
              <td mat-cell *matCellDef="let element" class="text-gray-500">
                {{ element.value }}
              </td>
            </ng-container>

            <ng-container matColumnDef="description">
              <th mat-header-cell *matHeaderCellDef>Beschrijving</th>
              <td mat-cell *matCellDef="let element" class="text-gray-500">
                {{ element.description ?? '—' }}
              </td>
            </ng-container>

            <ng-container matColumnDef="deleteAt">
              <th mat-header-cell *matHeaderCellDef mat-sort-header>Wordt verwijderd op</th>
              <td mat-cell *matCellDef="let element" class="text-gray-500">
                {{ formatDeleteAt(element.deleteAt!) }}
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let element">
                <div class="flex justify-end">
                  <button
                    *appHasPermission="'var:update'"
                    mat-stroked-button
                    type="button"
                    class="text-xs!"
                    (click)="handleRestore(element)"
                  >
                    <mat-icon>restore</mat-icon>
                    Herstel
                  </button>
                </div>
              </td>
            </ng-container>

            <tr
              mat-header-row
              *matHeaderRowDef="pendingDisplayedColumns; sticky: true"
              class="whitespace-nowrap"
            ></tr>
            <tr
              mat-row
              *matRowDef="let row; columns: pendingDisplayedColumns"
              class="hover:bg-gray-100! whitespace-nowrap"
            ></tr>
          </table>
        </app-data-table-container>
      </div>
      </div>
    </div>
  }
</div>
